import { z } from "zod";
// Reuse the canonical Json type + schema from the shared barrel's first module
// so `export *` from index.ts has exactly one `Json` / `jsonSchema` symbol.
import { jsonSchema } from "./service-requests";

/**
 * Brokerage (e-broker / وساطة) contracts shared contract (manifest-6).
 *
 * Single source of truth for the brokerage wire shape shared by the web SPA
 * (`src/repositories/brokerageContracts.ts`) and the NestJS slice
 * (`apps/api/src/brokerage-contracts/*`). Unlike the older Supabase-first slices
 * this domain is **Prisma-first** — NestJS is the system of record. The shape is
 * **snake_case** so the FE<->Nest transport is a field-for-field swap and the FE
 * `.parse()`s Nest responses against {@link brokerageContractSchema} at the flip
 * boundary (drift surfaces at runtime).
 *
 * TOLERANCE RULE (the #1699 silent-no-op lesson): own columns are pinned, but
 * the schema is `.passthrough()` so a new server column never rejects a valid
 * payload, and status/type fields stay open strings so a new enum value never
 * makes the FE silently fall back.
 *
 * NULLABILITY RULE: required core columns are pinned; EVERY other column is
 * uniformly `.nullish()` — null and absent are equivalent on the wire (the
 * server serializes null, the wizard sends null for cleared fields, and an
 * older API omitting a newer column must never reject). Don't mix `.nullable()`
 * / `.optional()` per-field — that drift is how wire bugs ship.
 */

/**
 * Contract lifecycle states. `active|expiring|expired` are DERIVED from the
 * contract dates by the status engine; `pending` (pre-activation) and `renew`
 * (superseded by a renewal) are explicit stored states. Advisory list — the row
 * keeps `status` an open string to never reject server data.
 */
export const BROKERAGE_CONTRACT_STATUSES = [
  "pending",
  "active",
  "expiring",
  "expired",
  "renew",
] as const;
export const brokerageContractStatusSchema = z.enum(BROKERAGE_CONTRACT_STATUSES);
export type BrokerageContractStatus =
  (typeof BROKERAGE_CONTRACT_STATUSES)[number];

// Brokerage-PDF upload cap, single source of truth for FE validation + API validatePdf — the two drifted once (20 vs 10 MB, #3614)
export const MAX_BROKERAGE_PDF_MB = 10;
export const MAX_BROKERAGE_PDF_BYTES = MAX_BROKERAGE_PDF_MB * 1024 * 1024;

/**
 * Effective-only sentinel: the subject (property or unit) has no live contract.
 * Never a STORED status — it only ever comes back from the status engine, which
 * is why it is not a member of BROKERAGE_CONTRACT_STATUSES. Mirrors the API's
 * `BROKERAGE_STATUS_NONE`.
 */
export const BROKERAGE_STATUS_NONE = "none";

/**
 * Operations a brokerage contract authorises for a property (the junction
 * `contract_type`). Advisory — kept an open string on the wire so a future op
 * type never rejects server data.
 */
export const BROKERAGE_OPS_TYPES = ["marketing", "sales", "management"] as const;
export const brokerageOpsTypeSchema = z.enum(BROKERAGE_OPS_TYPES);
export type BrokerageOpsType = (typeof BROKERAGE_OPS_TYPES)[number];

/**
 * Named access to the same values (`BrokerageOpsType.Marketing`) so callers stop
 * writing bare `"marketing"` literals. Value + type share the name, exactly as
 * the API's `BrokerageOperationType` does.
 */
export const BrokerageOpsType = {
  Marketing: "marketing",
  Sales: "sales",
  Management: "management",
} as const satisfies Record<string, BrokerageOpsType>;

/**
 * How far an authorising contract REACHES into a property: `full` (every unit)
 * vs `partial` (only SOME units — the property still reads `active`, but it is
 * NOT wholly brokered) vs `none`. Advisory — kept an open string on the wire so
 * a future value never rejects server data. Mirrors the API's `PropertyCoverage`
 * (apps/api/src/brokerage-contracts/enums/brokerage-coverage.enum.ts).
 */
export const BrokeragePropertyCoverage = {
  Full: "full",
  Partial: "partial",
  None: "none",
} as const;
export type BrokeragePropertyCoverage =
  (typeof BrokeragePropertyCoverage)[keyof typeof BrokeragePropertyCoverage];

/**
 * Where a UNIT's standing comes from: `unit` (a contract names this unit) vs
 * `property` (inherited from a whole-property contract) vs `none` (nothing
 * reaches it — including a contract scoped to a SIBLING unit). Lets the UI
 * explain WHY a unit is covered instead of merely asserting that it is.
 */
export const BrokerageUnitCoverage = {
  Unit: "unit",
  Property: "property",
  None: "none",
} as const;
export type BrokerageUnitCoverage =
  (typeof BrokerageUnitCoverage)[keyof typeof BrokerageUnitCoverage];

/**
 * A property↔contract link (junction row). AC2 — unit granularity: a
 * specific-unit link carries `unit_id`; a whole-property link has `unit_id`
 * null and `all_units` true. Both are `.nullish()` so an older API that omits
 * them never rejects (NULLABILITY RULE).
 */
export const propertyBrokerageLinkSchema = z
  .object({
    id: z.string(),
    company_id: z.string(),
    property_id: z.string(),
    brokerage_contract_id: z.string(),
    contract_type: z.string(),
    unit_id: z.string().nullish(),
    all_units: z.boolean().nullish(),
    created_at: z.string(),
    deleted_at: z.string().nullish(),
  })
  .passthrough();
export type PropertyBrokerageLink = z.infer<typeof propertyBrokerageLinkSchema>;

/**
 * A persisted brokerage contract row as the staff dashboard consumes it. Field
 * set + nullability mirror the Prisma `BrokerageContract` model serialized to
 * the snake_case wire shape. `property_links` is appended on detail reads.
 */
export const brokerageContractSchema = z
  .object({
    id: z.string(),
    company_id: z.string(),
    owner_id: z.string(),
    rega_contract_number: z.string(),
    deed_number: z.string().nullish(),
    // Contract meta (Inc-8 promoted fields)
    contract_execution_date: z.string().nullish(),
    place_city: z.string().nullish(),
    activity_type: z.string().nullish(),
    // Broker (party 1)
    broker_name: z.string().nullish(),
    broker_license_number: z.string().nullish(),
    broker_type: z.string().nullish(),
    broker_id_number: z.string().nullish(),
    broker_id_type: z.string().nullish(),
    broker_contact_no: z.string().nullish(),
    broker_email: z.string().nullish(),
    broker_national_address: z.string().nullish(),
    // Owner (party 2) + representative
    owner_name_on_contract: z.string().nullish(),
    owner_id_number: z.string().nullish(),
    owner_id_type: z.string().nullish(),
    owner_contact_no: z.string().nullish(),
    owner_email: z.string().nullish(),
    owner_national_address: z.string().nullish(),
    owner_representative_name: z.string().nullish(),
    owner_representative_id: z.string().nullish(),
    owner_representative_mobile: z.string().nullish(),
    owner_representative_poa_no: z.string().nullish(),
    // Property (Inc-8 promoted fields)
    property_registration_no: z.string().nullish(),
    deed_date: z.string().nullish(),
    property_address: z.string().nullish(),
    property_type: z.string().nullish(),
    property_use: z.string().nullish(),
    plan_no: z.string().nullish(),
    plot_no: z.string().nullish(),
    property_area: z.number().nullish(),
    min_unit_sale_price: z.number().nullish(),
    min_annual_rent: z.number().nullish(),
    property_services: z.string().nullish(),
    // Dates (date-only YYYY-MM-DD on the wire)
    start_date: z.string(),
    end_date: z.string(),
    duration_text: z.string().nullish(),
    // Commissions
    sales_commission_pct: z.number().nullish(),
    marketing_commission_pct: z.number().nullish(),
    includes_marketing: z.boolean(),
    payment_method: z.string().nullish(),
    broker_iban: z.string().nullish(),
    broker_bank_name: z.string().nullish(),
    status: z.string(),
    document_url: z.string().nullish(),
    extracted_data_json: jsonSchema.nullish(),
    manually_edited_fields: z.array(z.string()),
    renewed_from_id: z.string().nullish(),
    notes: z.string().nullish(),
    created_by: z.string().nullish(),
    updated_by: z.string().nullish(),
    created_at: z.string(),
    updated_at: z.string(),
    deleted_at: z.string().nullish(),
    // Appended on detail reads — the property links for this contract.
    property_links: z.array(propertyBrokerageLinkSchema).optional(),
  })
  .passthrough();
export type BrokerageContract = z.infer<typeof brokerageContractSchema>;

/**
 * Date-only wire format for contract dates. Mirrored by the Nest DTOs'
 * `@IsDateOnly` — a date the backend would reject fails here first with the
 * same shape requirement. `z.string().date()` checks calendar validity too
 * (2026-13-99 matches the regex but is not a date).
 */
export const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const dateOnlyString = z
  .string()
  .date("must be a real calendar date in YYYY-MM-DD format");

/**
 * Create payload sent to POST /brokerage-contracts. Server-managed and
 * audit-only columns are dropped; `includes_marketing` is derived server-side.
 * Only the four genuinely-required columns are mandatory; everything else is
 * `.nullish()` (the wizard sends null for cleared fields — see NULLABILITY
 * RULE above).
 */
export const createBrokerageContractSchema = z.object({
  owner_id: z.string(),
  rega_contract_number: z.string().min(1),
  start_date: dateOnlyString,
  end_date: dateOnlyString,
  // Contract meta (Inc-8)
  contract_execution_date: dateOnlyString.nullish(),
  place_city: z.string().nullish(),
  activity_type: z.string().nullish(),
  deed_number: z.string().nullish(),
  // Broker (party 1)
  broker_name: z.string().nullish(),
  broker_license_number: z.string().nullish(),
  broker_type: z.string().nullish(),
  broker_id_number: z.string().nullish(),
  broker_id_type: z.string().nullish(),
  broker_contact_no: z.string().nullish(),
  broker_email: z.string().nullish(),
  broker_national_address: z.string().nullish(),
  // Owner (party 2) + representative
  owner_name_on_contract: z.string().nullish(),
  owner_id_number: z.string().nullish(),
  owner_id_type: z.string().nullish(),
  owner_contact_no: z.string().nullish(),
  owner_email: z.string().nullish(),
  owner_national_address: z.string().nullish(),
  owner_representative_name: z.string().nullish(),
  owner_representative_id: z.string().nullish(),
  owner_representative_mobile: z.string().nullish(),
  owner_representative_poa_no: z.string().nullish(),
  // Property (Inc-8)
  property_registration_no: z.string().nullish(),
  deed_date: dateOnlyString.nullish(),
  property_address: z.string().nullish(),
  property_type: z.string().nullish(),
  property_use: z.string().nullish(),
  plan_no: z.string().nullish(),
  plot_no: z.string().nullish(),
  property_area: z.number().min(0).nullish(),
  min_unit_sale_price: z.number().min(0).nullish(),
  min_annual_rent: z.number().min(0).nullish(),
  property_services: z.string().nullish(),
  // Dates + commissions
  duration_text: z.string().nullish(),
  sales_commission_pct: z.number().min(0).max(100).nullish(),
  marketing_commission_pct: z.number().min(0).max(100).nullish(),
  payment_method: z.string().nullish(),
  broker_iban: z.string().nullish(),
  broker_bank_name: z.string().nullish(),
  status: brokerageContractStatusSchema.nullish(),
  document_url: z.string().nullish(),
  extracted_data_json: jsonSchema.nullish(),
  manually_edited_fields: z.array(z.string()).nullish(),
  renewed_from_id: z.string().nullish(),
  notes: z.string().nullish(),
});
export type CreateBrokerageContract = z.infer<
  typeof createBrokerageContractSchema
>;

/** Update payload sent to PUT /brokerage-contracts/:id — all fields optional. */
export const updateBrokerageContractSchema =
  createBrokerageContractSchema.partial();
export type UpdateBrokerageContract = z.infer<
  typeof updateBrokerageContractSchema
>;

/**
 * The field set a brokerage-contract PDF can yield to pre-fill the create
 * wizard's Review step — returned by POST /brokerage-contracts/extract. Every
 * field is optional + nullable: extraction is best-effort, so the endpoint may
 * return all-null and manual entry is the always-working path. `.passthrough()`
 * so a future extracted field never rejects a valid response (#1699 tolerance).
 *
 * This is the OCR-extractable SUBSET of {@link createBrokerageContractSchema}
 * (owner_id, status, document_url, snapshot/audit columns are NOT extracted).
 * The wizard records which of these the user changed into `manually_edited_fields`.
 */
export const extractedBrokerageFieldsSchema = z
  .object({
    // Contract
    rega_contract_number: z.string().nullish(),
    contract_execution_date: z.string().nullish(),
    start_date: z.string().nullish(),
    end_date: z.string().nullish(),
    duration_text: z.string().nullish(),
    place_city: z.string().nullish(),
    activity_type: z.string().nullish(),
    // Broker (party 1)
    broker_name: z.string().nullish(),
    broker_type: z.string().nullish(),
    broker_id_number: z.string().nullish(),
    broker_id_type: z.string().nullish(),
    broker_license_number: z.string().nullish(),
    broker_contact_no: z.string().nullish(),
    broker_email: z.string().nullish(),
    broker_national_address: z.string().nullish(),
    broker_iban: z.string().nullish(),
    broker_bank_name: z.string().nullish(),
    // Owner (party 2) + representative
    owner_name_on_contract: z.string().nullish(),
    owner_id_number: z.string().nullish(),
    owner_id_type: z.string().nullish(),
    owner_contact_no: z.string().nullish(),
    owner_email: z.string().nullish(),
    owner_national_address: z.string().nullish(),
    owner_representative_name: z.string().nullish(),
    owner_representative_id: z.string().nullish(),
    owner_representative_mobile: z.string().nullish(),
    owner_representative_poa_no: z.string().nullish(),
    // Property
    deed_number: z.string().nullish(),
    property_registration_no: z.string().nullish(),
    deed_date: z.string().nullish(),
    property_address: z.string().nullish(),
    property_type: z.string().nullish(),
    property_use: z.string().nullish(),
    plan_no: z.string().nullish(),
    plot_no: z.string().nullish(),
    property_area: z.number().nullish(),
    min_unit_sale_price: z.number().nullish(),
    min_annual_rent: z.number().nullish(),
    property_services: z.string().nullish(),
    // Commission
    sales_commission_pct: z.number().nullish(),
    marketing_commission_pct: z.number().nullish(),
    payment_method: z.string().nullish(),
    notes: z.string().nullish(),
  })
  .passthrough();
export type ExtractedBrokerageFields = z.infer<
  typeof extractedBrokerageFieldsSchema
>;

/** The keys a user may manually edit/override in the wizard's Review step. */
export const EXTRACTED_BROKERAGE_FIELD_KEYS = [
  // Contract
  "rega_contract_number",
  "contract_execution_date",
  "start_date",
  "end_date",
  "duration_text",
  "place_city",
  "activity_type",
  // Broker (party 1)
  "broker_name",
  "broker_type",
  "broker_id_number",
  "broker_id_type",
  "broker_license_number",
  "broker_contact_no",
  "broker_email",
  "broker_national_address",
  "broker_iban",
  "broker_bank_name",
  // Owner (party 2) + representative
  "owner_name_on_contract",
  "owner_id_number",
  "owner_id_type",
  "owner_contact_no",
  "owner_email",
  "owner_national_address",
  "owner_representative_name",
  "owner_representative_id",
  "owner_representative_mobile",
  "owner_representative_poa_no",
  // Property
  "deed_number",
  "property_registration_no",
  "deed_date",
  "property_address",
  "property_type",
  "property_use",
  "plan_no",
  "plot_no",
  "property_area",
  "min_unit_sale_price",
  "min_annual_rent",
  "property_services",
  // Commission
  "sales_commission_pct",
  "marketing_commission_pct",
  "payment_method",
  "notes",
] as const;
export type ExtractedBrokerageFieldKey =
  (typeof EXTRACTED_BROKERAGE_FIELD_KEYS)[number];

/**
 * Link a property to a contract for a given operation (junction insert).
 *
 * AC2 — the link covers EITHER specific units (`unit_ids`, one junction row
 * per unit) OR the whole property (`all_units: true`, or neither field).
 * `unit_ids` and `all_units:true` are mutually exclusive — the server rejects
 * sending both.
 */
export const linkPropertyBrokerageSchema = z.object({
  property_id: z.string(),
  contract_type: brokerageOpsTypeSchema,
  // .min(1) (not .nonempty()) so the inferred type stays string[] for callers
  // that build the array conditionally; the runtime "at least one" check holds.
  unit_ids: z.array(z.string()).min(1).optional(),
  all_units: z.boolean().optional(),
});
export type LinkPropertyBrokerage = z.infer<typeof linkPropertyBrokerageSchema>;

/**
 * The effective brokerage standing of a single property, computed by the status
 * engine: the effective contract status + the union of operations currently
 * authorised by that property's active/expiring contracts. Drives the
 * non-blocking soft-warn surfaces (ad publish, property card, owners dir).
 */
export const propertyBrokerageStatusSchema = z
  .object({
    property_id: z.string(),
    // Effective status across the property's contracts (the "best" one wins:
    // active > expiring > pending > renew > expired > none).
    status: z.string(),
    has_active_contract: z.boolean(),
    authorised_ops: z.array(z.string()),
    marketing_authorised: z.boolean(),
    sales_authorised: z.boolean(),
    // The contract driving the effective status, when there is one.
    contract_id: z.string().nullish(),
    // Date-only YYYY-MM-DD — matches start_date/end_date on the wire.
    expires_at: z.string().nullish(),
    // How much of the property the authorising contracts actually reach:
    // `full` (a property-wide contract) vs `partial` (only some units are
    // brokered — the property still reads `active`, but it is NOT wholly
    // covered) vs `none`. Kept an open string, like `status`, so a new value
    // never rejects server data. Nullish: an older API build omits them.
    coverage: z.string().nullish(),
    covered_unit_count: z.number().nullish(),
    total_unit_count: z.number().nullish(),
  })
  .passthrough();
export type PropertyBrokerageStatus = z.infer<
  typeof propertyBrokerageStatusSchema
>;

/**
 * The effective brokerage standing of a single UNIT.
 *
 * A unit is covered by its OWN unit-scoped contracts plus the whole-property
 * ones it inherits; a contract scoped to a sibling unit does not count.
 * `coverage` says which of the two is driving the verdict — `unit` (a contract
 * names this unit) or `property` (inherited from a property-wide contract) — so
 * the UI can explain WHY a unit is covered rather than just asserting it.
 */
export const unitBrokerageStatusSchema = z
  .object({
    unit_id: z.string(),
    property_id: z.string(),
    status: z.string(),
    has_active_contract: z.boolean(),
    authorised_ops: z.array(z.string()),
    marketing_authorised: z.boolean(),
    sales_authorised: z.boolean(),
    contract_id: z.string().nullish(),
    expires_at: z.string().nullish(),
    // 'unit' | 'property' | 'none' — open string, same reasoning as `status`.
    coverage: z.string().nullish(),
  })
  .passthrough();
export type UnitBrokerageStatus = z.infer<typeof unitBrokerageStatusSchema>;

/** Batched status request — POST /brokerage-contracts/status { property_ids }. */
export const batchPropertyStatusSchema = z.object({
  property_ids: z.array(z.string()),
});
export type BatchPropertyStatus = z.infer<typeof batchPropertyStatusSchema>;

/** Batched unit status — POST /brokerage-contracts/status/units { unit_ids }. */
export const batchUnitStatusSchema = z.object({
  unit_ids: z.array(z.string()),
});
export type BatchUnitStatus = z.infer<typeof batchUnitStatusSchema>;
